import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API,
  timeout: 30000,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("pulse_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// When a request returns 401 (invalid/expired token), wipe the local session
// so the UI no longer pretends the user is logged in. This commonly happens
// after a backend restart (in-memory SESSIONS dict is cleared) — without this,
// the sidebar still shows "אסף Owner" but every API call fails silently.
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    const path = error?.config?.url || "";
    if (status === 401 && !path.includes("/auth/login")) {
      localStorage.removeItem("pulse_token");
      localStorage.removeItem("pulse_user");
      // Hard reload to /login so React's auth context picks up the cleared state
      if (!window.location.pathname.startsWith("/login")) {
        window.location.href = "/login";
      }
    }
    return Promise.reject(error);
  }
);

export default api;

// === Auth ===
export const login = (username, password) => api.post("/auth/login", { username, password });
export const getMe = () => api.get("/auth/me");

// === Pulse data ===
export const getSummary = () => api.get("/summary");
export const getScore = () => api.get("/score");
export const getTopInsights = (limit = 3) => api.get(`/insights/top?limit=${limit}`);
export const getActionCenter = () => api.get("/action-center");
export const getFastGrowers = () => api.get("/insights/fast-growers");
export const getPriceVariance = () => api.get("/insights/price-variance");
export const getCostAnomalies = () => api.get("/insights/cost-anomalies");
export const getBranches = () => api.get("/insights/branches");
export const getTopSellers = (by = "revenue", limit = 10) =>
  api.get(`/insights/top-sellers?by=${by}&limit=${limit}`);
export const getSalesTrend = () => api.get("/insights/sales-trend");
export const getRevenueTimeseries = (days = 120) => api.get(`/insights/revenue-timeseries?days=${days}`);
export const getMonthlyRevenue = () => api.get("/insights/monthly-revenue");
export const getTopInvoices = (limit = 5) => api.get(`/insights/top-invoices?limit=${limit}`);
export const getLoyalInvoices = (limit = 5) => api.get(`/insights/loyal-invoices?limit=${limit}`);
export const getConcentration = () => api.get("/insights/concentration");
export const getDayOfWeek = () => api.get("/insights/day-of-week");
export const getHeadline = () => api.get("/insights/headline");
export const getPurchaseRecommendations = (limit = 8) => api.get(`/insights/purchase-recommendations?limit=${limit}`);
export const getCustomerChurn = (limit = 200) => api.get(`/insights/customer-churn?limit=${limit}`);
export const getDailyBrief = () => api.get("/daily-brief");
// /chat triggers router LLM → tools → synth LLM. End-to-end can be 30-60s
// on production (especially on mobile 4G). Default 30s axios timeout was
// causing "סליחה, משהו השתבש" errors on legitimate questions. Bumped to 90s,
// matching the other LLM endpoints (generateActionPlan / explainKpi).
//
// Kept as the buffered fallback. The streaming endpoint (sendChatStream)
// below is what the UI normally calls — it gives the user phase-by-phase
// progress so the 27s wait stops feeling like the screen is frozen.
export const sendChat = (sessionId, message) =>
  api.post("/chat", { session_id: sessionId, message }, { timeout: 90000 });

/**
 * Server-Sent Events streaming for chat — gives visible progress through
 * router → tools → synth → done phases.
 *
 * onPhase(evt) fires for every event from the backend. Phases:
 *   { phase: "router", label: string }
 *   { phase: "tools",  names: string[], label: string }
 *   { phase: "synth",  label: string }
 *   { phase: "done",   reply, tools_used, rationale, can_answer, transparency }
 *   { phase: "error",  detail: string, status: number }
 *
 * Uses fetch+ReadableStream rather than EventSource because EventSource
 * doesn't support custom headers (we need Authorization: Bearer ...).
 *
 * Returns a Promise that resolves when the stream closes (`done` or `error`).
 */
export const sendChatStream = async (sessionId, message, onPhase, { signal } = {}) => {
  const base = process.env.REACT_APP_BACKEND_URL;
  const token = localStorage.getItem("pulse_token");
  const resp = await fetch(`${base}/api/chat/stream`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({ session_id: sessionId, message }),
    signal,
  });
  if (!resp.ok || !resp.body) {
    // Surface a shape that mimics axios so the caller's catch block is happy.
    const err = new Error(`Chat stream failed: HTTP ${resp.status}`);
    err.response = { status: resp.status };
    throw err;
  }
  const reader = resp.body.getReader();
  const decoder = new TextDecoder("utf-8");
  let buffer = "";
  // Read until the server closes the stream.
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    // SSE events are separated by a blank line. Each line that starts with
    // "data: " is the JSON payload. We tolerate the rare case where two
    // events arrive in one chunk.
    let sep;
    while ((sep = buffer.indexOf("\n\n")) !== -1) {
      const raw = buffer.slice(0, sep);
      buffer = buffer.slice(sep + 2);
      const dataLine = raw.split("\n").find((l) => l.startsWith("data: "));
      if (!dataLine) continue;
      try {
        const evt = JSON.parse(dataLine.slice(6));
        onPhase(evt);
      } catch {
        // ignore malformed line
      }
    }
  }
};
export const executeAction = (insightId, payload) => api.post("/action/execute", { insight_id: insightId, payload });
export const getMarkedActions = () => api.get("/actions/marked");
export const removeMarkedAction = (id) => api.delete(`/actions/marked/${id}`);
export const getGreeting = () => api.get("/greeting");
export const getDemoHeadline = () => api.get("/headline");
export const getSavings = () => api.get("/savings");
export const getActivityFeed = () => api.get("/activity-feed");
export const getScanningStatus = () => api.get("/scanning-status");

// === AI helpers === (LLM calls can take 20-50s — override default timeout)
export const generateActionPlan = () => api.post("/ai/action-plan", {}, { timeout: 90000 });
export const explainKpi = (kpi, context = null) => api.post("/ai/explain", { kpi, context }, { timeout: 60000 });
export const searchSkus = (q, limit = 6) => api.get(`/search/skus?q=${encodeURIComponent(q)}&limit=${limit}`);

// === Inventory Intelligence ===
export const getInventoryKpis = () => api.get("/inventory/kpis");
export const getDeadCapitalBreakdown = () => api.get("/inventory/dead-capital-breakdown");

// Download the full dead-stock list as a UTF-8 CSV that Excel reads natively.
// Returns a Promise<{ filename, blob }> — the caller triggers the download.
export const exportDeadStockCSV = async () => {
  const res = await api.get("/inventory/dead-stock/export", {
    responseType: "blob",
  });
  // Extract filename from Content-Disposition header
  const cd = res.headers?.["content-disposition"] || "";
  const match = cd.match(/filename="?([^";]+)"?/);
  const filename = match ? match[1] : "dead_stock.csv";
  return { filename, blob: res.data };
};
export const getStockouts = (limit = 20) => api.get(`/inventory/stockouts?limit=${limit}`);
export const getCriticalRestock = (limit = 20) => api.get(`/inventory/critical-restock?limit=${limit}`);
export const getDeadStock = (limit = 20, minDays = 60) =>
  api.get(`/inventory/dead-stock?limit=${limit}&min_days=${minDays}`);
export const getOverstock = (limit = 20, minDays = 180) =>
  api.get(`/inventory/overstock?limit=${limit}&min_days=${minDays}`);
export const getNegativeBalance = (limit = 20) => api.get(`/inventory/negative-balance?limit=${limit}`);
export const getSkuInventory = (sku) => api.get(`/inventory/sku/${encodeURIComponent(sku)}`);

// === Data Quality & Procurement (First Scan cards) ===
export const getZeroPriceInvoices = (limit = 200) => api.get(`/data-quality/zero-price?limit=${limit}`);
export const getOnTheWay = (limit = 200) => api.get(`/procurement/on-the-way?limit=${limit}`);
export const getFamilyProfitability = (limit = 100) => api.get(`/families/profitability?limit=${limit}`);
export const getSmartRestock = (limit = 200) => api.get(`/insights/smart-restock?limit=${limit}`);
export const getExecutiveBrief = () => api.get(`/insights/executive-brief`);
export const getCherries = (limit = 5) => api.get(`/insights/cherries?limit=${limit}`);
export const getSuggestedQuestions = () => api.get(`/ai/suggested-questions`);
export const getExecutiveActions = () => api.post(`/ai/executive-actions`, {}, { timeout: 90000 });
export const getWhatChanged = () => api.get(`/insights/what-changed`);

// === Tracking ===
export const track = (event, page = null, detail = null) =>
  api.post("/track", { event, page, detail }).catch(() => {});

// === Admin ===
export const getAdminAnalytics = (includeOwner = false, tenant = null) => {
  const params = new URLSearchParams();
  if (includeOwner) params.set("include_owner", "1");
  if (tenant) params.set("tenant", tenant);
  const qs = params.toString();
  return api.get(`/admin/analytics${qs ? `?${qs}` : ""}`);
};
export const getAdminUsers = () => api.get("/admin/users");

// === Feedback (implementer / tester) ===
export const submitFeedback = (message, page = null, context = null) =>
  api.post("/feedback", { message, page, context });
export const getAdminFeedback = (status = null, tenant = null) => {
  const params = new URLSearchParams();
  if (status) params.set("status", status);
  if (tenant) params.set("tenant", tenant);
  const qs = params.toString();
  return api.get(`/admin/feedback${qs ? `?${qs}` : ""}`);
};
export const resolveFeedback = (id) =>
  api.patch(`/admin/feedback/${id}/resolve`);
export const reopenFeedback = (id) =>
  api.patch(`/admin/feedback/${id}/reopen`);
